import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useTheme } from "../contexts/ThemeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  User, 
  Palette, 
  Globe, 
  Check, 
  Loader2,
  Sun,
  Moon,
  Link as LinkIcon,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsPage() {
  const { user, checkAuth } = useAuth();
  const { t, language, changeLanguage, languages } = useLanguage();
  const { theme, toggleTheme, setTheme } = useTheme();

  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    bio: user?.bio || "",
    store_slug: user?.store_slug || "",
  });
  const [saving, setSaving] = useState(false);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/profile`, profileData);
      await checkAuth();
      toast.success(t("settings.profileUpdated"));
    } catch (error) {
      console.error("Failed to update profile:", error);
      toast.error(error.response?.data?.detail || t("settings.failedToUpdate"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8 max-w-4xl animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("settings.title")}</h1>
        <p className="text-muted-foreground mt-1">
          {t("settings.subtitle")}
        </p>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-[400px] h-11 sm:h-12 rounded-xl p-1">
          <TabsTrigger value="profile" className="rounded-lg data-[state=active]:shadow-sm text-xs sm:text-sm" data-testid="tab-profile">
            <User className="h-4 w-4 mr-1 sm:mr-2" />
            {t("settings.profile")}
          </TabsTrigger>
          <TabsTrigger value="appearance" className="rounded-lg data-[state=active]:shadow-sm text-xs sm:text-sm" data-testid="tab-appearance">
            <Palette className="h-4 w-4 mr-1 sm:mr-2" />
            {t("settings.appearance")}
          </TabsTrigger>
          <TabsTrigger value="language" className="rounded-lg data-[state=active]:shadow-sm text-xs sm:text-sm" data-testid="tab-language">
            <Globe className="h-4 w-4 mr-1 sm:mr-2" />
            {t("settings.language")}
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-8">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-primary/10">
                  <User className="h-5 w-5 text-primary" />
                </div>
                {t("settings.profile")}
              </CardTitle>
              <CardDescription>{t("settings.profileDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileSubmit} className="space-y-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">{t("auth.name")}</Label>
                  <Input
                    id="name"
                    value={profileData.name}
                    onChange={(e) =>
                      setProfileData({ ...profileData, name: e.target.value })
                    }
                    className="h-12 rounded-xl"
                    data-testid="settings-name-input"
                  />
                </div>

                {/* Bio */}
                <div className="space-y-2">
                  <Label htmlFor="bio" className="text-sm font-medium">{t("settings.bio")}</Label>
                  <Textarea
                    id="bio"
                    value={profileData.bio}
                    onChange={(e) =>
                      setProfileData({ ...profileData, bio: e.target.value })
                    }
                    placeholder={t("settings.bioPlaceholder")}
                    rows={4}
                    className="rounded-xl resize-none"
                    data-testid="settings-bio-input"
                  />
                </div>

                {/* Store Slug */}
                <div className="space-y-2">
                  <Label htmlFor="store_slug" className="text-sm font-medium">{t("settings.storeSlug")}</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center h-12 px-3 sm:px-4 rounded-xl bg-muted text-muted-foreground text-xs sm:text-sm shrink-0 max-w-[55%] sm:max-w-none overflow-hidden">
                      <LinkIcon className="h-4 w-4 mr-2 shrink-0" />
                      <span className="truncate">{window.location.origin}/store/</span>
                    </div>
                    <Input
                      id="store_slug"
                      value={profileData.store_slug}
                      onChange={(e) =>
                        setProfileData({
                          ...profileData,
                          store_slug: e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9-]/g, ""),
                        })
                      }
                      className="flex-1 h-12 rounded-xl min-w-0"
                      data-testid="settings-slug-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  disabled={saving} 
                  className="h-12 px-8 rounded-xl shadow-lg shadow-primary/20"
                  data-testid="save-profile-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t("settings.saving")}
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      {t("settings.saveChanges")}
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Appearance Tab */}
        <TabsContent value="appearance" className="mt-8">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Palette className="h-5 w-5 text-purple-500" />
                </div>
                {t("settings.appearance")}
              </CardTitle>
              <CardDescription>{t("settings.appearanceDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
                <div>
                  <Label className="text-base font-medium">
                    {theme === "dark" ? t("settings.darkMode") : t("settings.lightMode")}
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("settings.toggleTheme")}
                  </p>
                </div>
                <Switch
                  checked={theme === "dark"}
                  onCheckedChange={toggleTheme}
                  data-testid="theme-switch"
                />
              </div>

              <div className="grid grid-cols-2 gap-6">
                <button
                  type="button"
                  onClick={() => setTheme("light")}
                  className={`p-6 rounded-2xl border-2 transition-all duration-200 ${
                    theme === "light"
                      ? "border-primary bg-primary/5 shadow-lg"
                      : "border-border hover:border-muted-foreground"
                  }`}
                >
                  <div className="w-full aspect-video bg-white rounded-xl border border-slate-200 mb-4 flex items-center justify-center shadow-inner">
                    <Sun className="h-8 w-8 text-amber-500" />
                  </div>
                  <p className="text-sm font-semibold">{t("settings.lightMode")}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t("settings.cleanBright")}</p>
                </button>
                <button
                  type="button"
                  onClick={() => setTheme("dark")}
                  className={`p-6 rounded-2xl border-2 transition-all duration-200 ${
                    theme === "dark"
                      ? "border-primary bg-primary/5 shadow-lg"
                      : "border-border hover:border-muted-foreground"
                  }`}
                >
                  <div className="w-full aspect-video bg-[#0A1424] rounded-xl border border-[#1E293B] mb-4 flex items-center justify-center">
                    <Moon className="h-8 w-8 text-slate-400" />
                  </div>
                  <p className="text-sm font-semibold">{t("settings.darkMode")}</p>
                  <p className="text-xs text-muted-foreground mt-1">{t("settings.easyOnEyes")}</p>
                </button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Language Tab */}
        <TabsContent value="language" className="mt-8">
          <Card className="border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Globe className="h-5 w-5 text-blue-500" />
                </div>
                {t("settings.language")}
              </CardTitle>
              <CardDescription>{t("settings.languageDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="text-sm font-medium">{t("settings.interfaceLanguage")}</Label>
                <Select value={language} onValueChange={changeLanguage}>
                  <SelectTrigger className="w-full md:w-72 h-12 rounded-xl" data-testid="language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map((lang) => (
                      <SelectItem key={lang.code} value={lang.code}>
                        <span className="flex items-center gap-3">
                          <span className="text-lg">{lang.flag}</span>
                          {lang.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    onClick={() => changeLanguage(lang.code)}
                    className={`p-6 rounded-2xl border-2 transition-all duration-200 ${
                      language === lang.code
                        ? "border-primary bg-primary/5 shadow-lg"
                        : "border-border hover:border-muted-foreground"
                    }`}
                    data-testid={`lang-btn-${lang.code}`}
                  >
                    <span className="text-4xl mb-3 block">{lang.flag}</span>
                    <p className="text-sm font-semibold">{lang.name}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
